import React from 'react';
import { useTranslation } from 'react-i18next';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';

function formatDate(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '-';
  }

  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? raw : date.toLocaleString();
}

function getStatusBadgeClass(status) {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'resolved') {
    return 'bg-success-subtle text-success';
  }
  if (normalized === 'holding_response_sent') {
    return 'bg-info-subtle text-info';
  }
  if (normalized === 'acknowledged') {
    return 'bg-primary-subtle text-primary';
  }
  return 'bg-warning-subtle text-warning';
}

function ComplaintResolveModal({ modalState, onClose, onChange, onSubmit }) {
  const { t } = useTranslation();

  if (!modalState.isOpen) {
    return null;
  }

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{t('complianceComplaints.resolveModalTitle')}</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={modalState.isSubmitting}></button>
            </div>
            <form onSubmit={onSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">{t('complianceComplaints.labelResult')}</label>
                  <select
                    className="form-select"
                    value={modalState.form.result}
                    onChange={(event) => onChange('result', event.target.value)}
                    disabled={modalState.isSubmitting}
                  >
                    <option value="upheld">{t('complianceComplaints.resultUpheld')}</option>
                    <option value="rejected">{t('complianceComplaints.resultRejected')}</option>
                    <option value="partially_upheld">{t('complianceComplaints.resultPartiallyUpheld')}</option>
                  </select>
                </div>
                <div className="mb-0">
                  <label className="form-label">{t('complianceComplaints.labelDetails')}</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={modalState.form.details}
                    onChange={(event) => onChange('details', event.target.value)}
                    disabled={modalState.isSubmitting}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={modalState.isSubmitting}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={modalState.isSubmitting}>
                  {modalState.isSubmitting ? t('complianceComplaints.resolving') : t('complianceComplaints.resolveButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

function ComplianceComplaintsPageView({
  report,
  isLoading,
  isSubmitting,
  activeActionUid,
  createForm,
  resolveModal,
  error,
  success,
  onCreateFormChange,
  submitCreate,
  acknowledgeComplaint,
  sendHoldingResponse,
  openResolve,
  closeResolve,
  onResolveFormChange,
  submitResolve,
}) {
  const { t } = useTranslation();

  return (
    <div>
      <FloatingAlerts error={error} success={success} />
      <PageHeader title={t('complianceComplaints.title')} subtitle={t('complianceComplaints.subtitle')} />

      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <div className="card card-body">
            <div className="text-muted text-uppercase small mb-1">{t('complianceComplaints.statOpen')}</div>
            <div className="fs-4 fw-semibold">{report.openCount}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card card-body">
            <div className="text-muted text-uppercase small mb-1">{t('complianceComplaints.statResolved')}</div>
            <div className="fs-4 fw-semibold">{report.resolvedCount}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card card-body">
            <div className="text-muted text-uppercase small mb-1">{t('complianceComplaints.statOverdue')}</div>
            <div className="fs-4 fw-semibold text-danger">{report.overdueCount}</div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-xl-4">
          <div className="card card-body">
            <h5 className="mb-3">{t('complianceComplaints.createComplaint')}</h5>
            <form onSubmit={submitCreate}>
              <div className="mb-3">
                <label className="form-label">{t('complianceComplaints.labelProfileUid')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={createForm.profileUid}
                  onChange={(event) => onCreateFormChange('profileUid', event.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">{t('complianceComplaints.labelSourceTicketUid')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={createForm.sourceTicketUid}
                  onChange={(event) => onCreateFormChange('sourceTicketUid', event.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">{t('complianceComplaints.labelCategory')}</label>
                <select
                  className="form-select"
                  value={createForm.category}
                  onChange={(event) => onCreateFormChange('category', event.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="general">{t('complianceComplaints.categoryGeneral')}</option>
                  <option value="service">{t('complianceComplaints.categoryService')}</option>
                  <option value="payment">{t('complianceComplaints.categoryPayment')}</option>
                  <option value="trading">{t('complianceComplaints.categoryTrading')}</option>
                  <option value="compliance">{t('complianceComplaints.categoryCompliance')}</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">{t('complianceComplaints.labelDeadlineAt')}</label>
                <input
                  type="date"
                  className="form-control"
                  value={createForm.deadlineAt}
                  onChange={(event) => onCreateFormChange('deadlineAt', event.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">{t('complianceComplaints.labelDetails')}</label>
                <textarea
                  className="form-control"
                  rows={5}
                  value={createForm.details}
                  onChange={(event) => onCreateFormChange('details', event.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className="d-flex justify-content-end">
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? t('complianceComplaints.creating') : t('complianceComplaints.createComplaintButton')}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="col-xl-8">
          <div className="card card-body">
            {isLoading ? <div className="text-muted">{t('complianceComplaints.loading')}</div> : null}
            {!isLoading && report.items.length === 0 ? <div className="text-muted">{t('complianceComplaints.noComplaints')}</div> : null}
            {!isLoading && report.items.length > 0 ? (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>{t('complianceComplaints.colCategory')}</th>
                      <th>{t('complianceComplaints.colStatus')}</th>
                      <th>{t('complianceComplaints.colProfileUid')}</th>
                      <th>{t('complianceComplaints.colDeadline')}</th>
                      <th>{t('complianceComplaints.colCreated')}</th>
                      <th>{t('complianceComplaints.colDetails')}</th>
                      <th className="text-end">{t('complianceComplaints.colActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.map((item) => (
                      <tr key={item.uid || `${item.category}-${item.created}`}>
                        <td className="text-capitalize">{item.category || '-'}</td>
                        <td>
                          <span className={`badge rounded-pill fw-semibold text-uppercase ${getStatusBadgeClass(item.status)}`}>
                            {item.status || '-'}
                          </span>
                        </td>
                        <td className="text-break">{item.profileUid || '-'}</td>
                        <td>{formatDate(item.deadlineAt)}</td>
                        <td>{formatDate(item.created)}</td>
                        <td className="text-break">{item.details || '-'}</td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm" role="group">
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={() => acknowledgeComplaint(item.uid)}
                              disabled={activeActionUid === item.uid || item.status !== 'open'}
                            >
                              {t('complianceComplaints.btnAck')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-info"
                              onClick={() => sendHoldingResponse(item.uid)}
                              disabled={activeActionUid === item.uid || item.status === 'resolved'}
                            >
                              {t('complianceComplaints.btnHolding')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-success"
                              onClick={() => openResolve(item.uid)}
                              disabled={item.status === 'resolved'}
                            >
                              {t('complianceComplaints.btnResolve')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <ComplaintResolveModal
        modalState={resolveModal}
        onClose={closeResolve}
        onChange={onResolveFormChange}
        onSubmit={submitResolve}
      />
    </div>
  );
}

export default ComplianceComplaintsPageView;
